import type { HydrateMode } from './profile-sync';
import type { Profile } from './state';

export interface Arrival {
  mode: HydrateMode;
  /** What this device has cached. */
  profile: Pick<Profile, 'uid' | 'madeDays' | 'introSeen'>;
  /** The url is an email sign-in link: boot has a save or a sign-in to finish. */
  emailLink: boolean;
  /** Auth has somebody stored on this device (read after auth has initialised). */
  signedIn: boolean;
}

/**
 * Somebody new on this device: no cached road, nobody signed in, and no save
 * or sign-in coming back through the url. Their anonymous identity does not
 * exist yet, so the server cannot hold a road for them and boot has nothing
 * worth waiting for — yet on a phone that wait was 4–6 s of pulse before the
 * door (Lighthouse, 2026-10-08). They get the door at once and boot carries on
 * behind it. A Google redirect coming back always has somebody signed in (the
 * anonymous user being linked), so it never passes. Pure, for the test.
 */
export function isNewHere(a: Arrival): boolean {
  if (a.mode !== 'merge') return false; // ?fresh and DEV ?day=N have their own work to do first
  const p = a.profile;
  if (p.uid || p.madeDays > 0 || p.introSeen) return false;
  return !a.emailLink && !a.signedIn;
}

/**
 * Day 1 begun and not finished: the intro seen, nothing made yet. Somebody
 * like that who lands on the front door is coming back to the warm-up, and
 * goes back into it rather than onto Today's tiles, which skip Day 1's story
 * and its reward.
 *
 * The case it exists for (2026-10-14): Facebook's in-app browser reloads a page
 * left in the background from the ad's url, so a visitor who switched away in
 * the middle of the warm-up came back to `/`, not `/start`. FrontDoor acts on
 * it only for the url the page was loaded on, never for a tap on Today, so the
 * warm-up's own way out still goes somewhere.
 */
export function dayOneUnderway(p: Pick<Profile, 'introSeen' | 'madeDays' | 'made'>): boolean {
  return !!p.introSeen && p.madeDays === 0 && !p.made?.length;
}
